import { Check, ChevronDown, Factory } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group'
import { cn } from '../../../lib/utils'
import { siteOptionLabel } from './siteIds'
import { WizardChrome } from './WizardChrome'
import type { IngestWizardApi } from './useIngestWizard'

interface SiteOption {
  id: string
  name: string
  meta?: string
}

interface StepSiteProps {
  wizard: IngestWizardApi
  sites: SiteOption[]
  sitesLoading?: boolean
}

export function StepSite({ wizard, sites, sitesLoading }: StepSiteProps) {
  const {
    state,
    step1Valid,
    step1Saving,
    runNameError,
    setRunName,
    setMode,
    setNewSiteName,
    pickExistingSite,
    goStep2
  } = wizard
  const siteLocked = state.launched.size > 0
  const runNameTooLong = Array.from(state.runName.trim()).length > 100

  return (
    <WizardChrome
      eyebrow="Step 01 — Name & site"
      title="Choose a site and optionally name this ingest"
      description="A name can make this run easier to find later. Everything ingested in the run is scoped to one site."
      onContinue={() => void goStep2()}
      continueDisabled={!step1Valid || step1Saving}
    >
      <div className="mb-4">
        <Label htmlFor="ingest-run-name" className="text-11 tracking-[0.08em] text-ink-3 uppercase">
          Run name (optional)
        </Label>
        <Input
          id="ingest-run-name"
          value={state.runName}
          onChange={(event) => setRunName(event.target.value)}
          placeholder="e.g. Ridgeline controls baseline"
          aria-invalid={runNameTooLong || Boolean(runNameError)}
          aria-describedby="ingest-run-name-help"
          className="mt-1.5 max-w-md"
        />
        <p
          id="ingest-run-name-help"
          className={cn(
            'mt-1.5 text-xs',
            runNameTooLong || runNameError ? 'text-st-down' : 'text-ink-3'
          )}
        >
          {runNameTooLong
            ? 'Run name must be 100 characters or fewer.'
            : runNameError
              ? runNameError
              : 'If provided, the run name must be unique in your organization.'}
        </p>
      </div>

      <RadioGroup
        value={state.mode ?? ''}
        disabled={siteLocked}
        onValueChange={(v) => {
          if (v === 'new' || v === 'existing') setMode(v)
        }}
        className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        <label
          className={cn(
            'flex cursor-pointer gap-2.5 rounded-lg border bg-bg-elev px-3 py-2.5 text-left transition-colors',
            state.mode === 'new' ? 'border-brand bg-brand-soft/40' : 'border-line hover:bg-bg-sunk'
          )}
        >
          <RadioGroupItem value="new" className="mt-0.5" />
          <span>
            <span className="block text-14 font-medium text-ink">Add a new site</span>
            <span className="mt-0.5 block text-xs leading-snug text-ink-3">
              Nothing has been ingested for this plant yet.
            </span>
          </span>
        </label>
        <label
          className={cn(
            'flex cursor-pointer gap-2.5 rounded-lg border bg-bg-elev px-3 py-2.5 text-left transition-colors',
            state.mode === 'existing'
              ? 'border-brand bg-brand-soft/40'
              : 'border-line hover:bg-bg-sunk'
          )}
        >
          <RadioGroupItem value="existing" className="mt-0.5" />
          <span>
            <span className="block text-14 font-medium text-ink">Modify an existing site</span>
            <span className="mt-0.5 block text-xs leading-snug text-ink-3">
              Change one resource on a site already in the ontology.
            </span>
          </span>
        </label>
      </RadioGroup>

      {state.mode === 'new' ? (
        <div className="mt-4 border-t border-line pt-3">
          <Label htmlFor="site-name" className="text-11 tracking-[0.08em] text-ink-3 uppercase">
            Site name
          </Label>
          <Input
            id="site-name"
            value={state.newSiteName}
            disabled={siteLocked}
            onChange={(e) => setNewSiteName(e.target.value)}
            placeholder="e.g. Ridgeline Works"
            className="mt-1.5 max-w-md"
          />
          <p className="mt-1.5 text-xs text-ink-3">
            The site is created when this ingest runs. Areas, lines, and equipment are inferred from
            what you upload.
          </p>
        </div>
      ) : null}

      {state.mode === 'existing' ? (
        <div className="mt-4 border-t border-line pt-3">
          <div className="text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">Site</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="mt-1.5 h-8 w-full max-w-md justify-between font-normal"
                disabled={sitesLoading || siteLocked}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Factory className="size-3.5 shrink-0 text-ink-3" />
                  <span
                    className={cn('truncate', state.existingSiteName ? 'text-ink' : 'text-ink-3')}
                  >
                    {sitesLoading
                      ? 'Loading sites…'
                      : state.existingSiteName
                        ? siteOptionLabel(state.existingSiteName, state.existingSiteId ?? '')
                        : 'Select a site'}
                  </span>
                </span>
                <ChevronDown className="size-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[var(--radix-dropdown-menu-trigger-width)]"
              align="start"
            >
              {sites.length === 0 ? (
                <DropdownMenuItem disabled>No sites in the ontology yet</DropdownMenuItem>
              ) : (
                sites.map((site) => (
                  <DropdownMenuItem
                    key={site.id}
                    onClick={() => pickExistingSite(site.id, site.name)}
                    className="gap-2"
                  >
                    <Factory className="size-3.5 text-ink-3" />
                    <span className="min-w-0 flex-1 truncate">
                      {siteOptionLabel(site.name, site.id)}
                    </span>
                    {site.meta ? (
                      <span className="font-mono text-11 text-ink-3">{site.meta}</span>
                    ) : null}
                    {state.existingSiteId === site.id ? (
                      <Check className="size-3.5 shrink-0" />
                    ) : null}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <p className="mt-2 text-xs text-ink-3">
            A modify session changes one resource at a time — you pick the resource and the action
            next.
          </p>
        </div>
      ) : null}
      {siteLocked ? (
        <p className="mt-3 text-xs text-st-warn">
          The site cannot be changed after the first source launches because completed jobs already
          belong to its ingest branch.
        </p>
      ) : null}
    </WizardChrome>
  )
}
